import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { Button, Flex, Form, Typography, Spin, Alert } from "antd";
import { useStores } from "stores";
import { parseErrorMessage } from "utils";
import { getStatusMessage, updateNotificationSettings } from "services";
import { DefaultCheckbox, WhiteBoxBlock } from "ui";
import { NOTIFICATION_TYPE_ENUM, NotificationSettingsDto } from "interfaces";
import { useNotificationContext } from "providers";

import styles from "./notification.module.css";

export const NotificationSettingsBlock = observer(() => {
  const { notificationSettings, user } = useStores();
  const [form] = Form.useForm<NotificationSettingsDto>();
  const { openNotification } = useNotificationContext();

  const userId = user.userInfo?.id;
  const companyId = user.userInfo?.company_id;

  const {
    isCustomer,
    isRiskManager,
    isProvider,
    isSeniorOperator,
    isJuniorOperator,
    isOperator,
    isSystemAdmin,
    isUserAdmin,
  } = user.roles;

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userId && companyId) {
      notificationSettings.fetchNotificationSettings(userId, companyId).then(() => {
        form.setFieldsValue(notificationSettings.settings);
      });
    }
  }, [companyId, form, notificationSettings, userId]);

  const canChangeBasicSettings = isCustomer || isProvider || isSeniorOperator || isJuniorOperator || isRiskManager;

  const onFinish = async (values: NotificationSettingsDto) => {
    if (companyId && userId) {
      setLoading(true);
      await updateNotificationSettings({ companyId, userId }, values)
        .then(() => {
          openNotification({
            message: "Запрос успешно отправлен",
            type: NOTIFICATION_TYPE_ENUM.SUCCESS,
          });
          setTimeout(async () => {
            await notificationSettings.updateSettingsQuietly(userId, companyId).then((updatedSettings) => {
              if (updatedSettings) {
                form.setFieldsValue(updatedSettings);
              }
            });
            setLoading(false);
          }, 1500);
        })
        .catch((error) => {
          openNotification({
            description: parseErrorMessage(error),
            type: NOTIFICATION_TYPE_ENUM.ERROR,
          });
          setLoading(false);
        });
    }
  };

  return (
    <WhiteBoxBlock className={styles.notificationBlockBox}>
      <Flex vertical gap="large">
        <Typography.Text className="whitebox-title">Уведомления</Typography.Text>

        <Spin spinning={notificationSettings.isPending}>
          {notificationSettings.fetchStatus === "success" && (
            <Form form={form} onFinish={onFinish}>
              <Flex className={styles.notificationBlock} gap="large">
                {(isCustomer || isProvider || isSeniorOperator || isJuniorOperator || isRiskManager) && (
                  <Flex vertical gap="small">
                    <Typography.Text className={styles.notificationTitle}>Уведомления на почту</Typography.Text>
                    <Flex vertical gap="small" className={styles.mailNotificationValues}>
                      {canChangeBasicSettings && (
                        <Flex vertical gap="small">
                          <Form.Item name="emailRequestStatusChange" valuePropName="checked" noStyle>
                            <DefaultCheckbox disabled={loading}>Смена статуса заявки</DefaultCheckbox>
                          </Form.Item>
                          {isCustomer && (
                            <Form.Item name="emailDocumentUpload" valuePropName="checked" noStyle>
                              <DefaultCheckbox disabled={loading}>Загрузка закрывающих документов</DefaultCheckbox>
                            </Form.Item>
                          )}
                          {(isCustomer || isSeniorOperator || isJuniorOperator) && (
                            <Form.Item name="emailContractStatusChange" valuePropName="checked" noStyle>
                              <DefaultCheckbox disabled={loading}>Смена статуса договора</DefaultCheckbox>
                            </Form.Item>
                          )}

                          {!isOperator && (
                            <Form.Item name="emailNewRubOperation" valuePropName="checked" noStyle>
                              <DefaultCheckbox disabled={loading}>Движение средств по счету</DefaultCheckbox>
                            </Form.Item>
                          )}
                        </Flex>
                      )}
                    </Flex>
                  </Flex>
                )}
                <Flex vertical gap="small">
                  <Typography.Text className={styles.notificationTitle}>Уведомления безопасности</Typography.Text>

                  <Form.Item name="emailPlatformLogin" valuePropName="checked" noStyle>
                    <DefaultCheckbox disabled={loading}>Вход на платформу</DefaultCheckbox>
                  </Form.Item>
                  <Form.Item name="emailPasswordChange" valuePropName="checked" noStyle>
                    <DefaultCheckbox disabled={loading}>Изменение пароля</DefaultCheckbox>
                  </Form.Item>
                </Flex>
                {canChangeBasicSettings && (
                  <Flex vertical gap="small">
                    <Typography.Text className={styles.notificationTitle}>
                      Отображение подсказок в интерфейсе
                    </Typography.Text>
                    <Flex vertical gap="small">
                      <Form.Item name="uiRequestStatusChange" valuePropName="checked" noStyle>
                        <DefaultCheckbox disabled={loading}>Смена статуса заявки</DefaultCheckbox>
                      </Form.Item>
                      <Form.Item name="uiTxUpload" valuePropName="checked" noStyle>
                        <DefaultCheckbox disabled={loading}>Смена статуса блокчейн транзакции</DefaultCheckbox>
                      </Form.Item>
                    </Flex>
                  </Flex>
                )}
              </Flex>
              <Button type="primary" htmlType="submit" loading={loading} className={styles.submitButton} size="middle">
                Сохранить
              </Button>
            </Form>
          )}
          {notificationSettings.fetchStatus === "error" && notificationSettings.fetchError && (
            <Alert
              message={getStatusMessage(notificationSettings.fetchError.status)}
              description={parseErrorMessage(notificationSettings.fetchError)}
              type="error"
              showIcon
            />
          )}
        </Spin>
      </Flex>
    </WhiteBoxBlock>
  );
});
